import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { ErrorState } from '@vta/ui';
import type { StoryStatus } from '@vta/types';
import { RequireAdmin } from '../components/RequireAdmin.tsx';
import { apiClient, ApiFetchError } from '../lib/api-client.ts';
import {
  SubmissionReviewTable,
  type AdminSubmission,
} from '../features/admin/SubmissionReviewTable.tsx';
import { SubmissionDetailPanel } from '../features/admin/SubmissionDetailPanel.tsx';

export const Route = createFileRoute('/admin/submissions')({
  component: AdminSubmissionsPage,
});

interface ListResponse {
  submissions: AdminSubmission[];
}

function AdminSubmissionsPage() {
  return (
    <RequireAdmin>
      <SubmissionsView />
    </RequireAdmin>
  );
}

function SubmissionsView() {
  const [status, setStatus] = React.useState<StoryStatus>('pending');
  const [data, setData] = React.useState<AdminSubmission[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<ListResponse>('/api/admin/submissions', {
        query: { status, limit: 100 },
      });
      setData(res.submissions);
    } catch (err) {
      setError(err instanceof ApiFetchError ? err.message : 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [status]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleMutated = React.useCallback(
    (next: { id: string; status: StoryStatus } | null) => {
      if (!next) return;
      // If the row's new status no longer matches the current filter, drop it
      // from the table; otherwise patch in place. Refetch in the background to
      // catch admin's own edits to body/note.
      setData((prev) =>
        next.status !== status
          ? prev.filter((p) => p.id !== next.id)
          : prev.map((p) => (p.id === next.id ? { ...p, status: next.status } : p)),
      );
      void refresh();
    },
    [refresh, status],
  );

  return (
    <>
      <Helmet>
        <title>Submissions · Admin · VEX Teams Atlas</title>
      </Helmet>
      <div className="mx-auto w-full max-w-6xl px-4 py-10 md:py-14">
        <header className="mb-8 flex flex-col gap-2">
          <p className="text-2xs uppercase tracking-[0.32em] text-fg-subtle">Admin</p>
          <h1 className="font-display text-3xl tracking-tight text-fg md:text-4xl">
            Submissions queue
          </h1>
        </header>

        {error ? <ErrorState title="Couldn't load queue" description={error} retry={refresh} /> : null}

        <SubmissionReviewTable
          submissions={data}
          status={status}
          onStatusChange={(s) => setStatus(s)}
          selectedId={selectedId}
          onSelect={(id) => setSelectedId(id)}
          isLoading={loading}
        />

        <SubmissionDetailPanel
          submissionId={selectedId}
          onClose={() => setSelectedId(null)}
          onMutated={handleMutated}
        />
      </div>
    </>
  );
}
